/**
 * Banner shown when the signed-in user still has an open check-in past
 * (office_hours_end + 30 min). Polls /attendance/today every 5 minutes.
 *
 * Two CTAs:
 *   - "Still working" → dismiss for this session
 *   - "Check out now" → POST /attendance/checkout and dismiss
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Clock, X } from "lucide-react";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";

function parseHHMM(s) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s || "");
  if (!m) return [18, 30];
  return [Number(m[1]), Number(m[2])];
}

export default function OpenCheckInReminder() {
  const { user, settings } = useAuth();
  const [today, setToday] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem("pulse-checkin-reminder-dismissed") === "1";
    } catch { return false; }
  });

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/attendance/today");
      setToday(r.data || null);
    } catch { setToday(null); }
  }, []);

  useEffect(() => {
    if (!user) return;
    refresh();
    const t = setInterval(refresh, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(t);
  }, [user, refresh]);

  if (!user || dismissed || !today || !today.check_in || today.check_out) return null;

  // Compute trigger time client-side using office_hours_end from settings
  const [hh, mm] = parseHHMM(settings?.office_hours_end || "18:30");
  const trigger = new Date();
  trigger.setHours(hh, mm + 30, 0, 0);
  if (new Date() < trigger) return null;

  const checkoutNow = async () => {
    try {
      navigator.geolocation?.getCurrentPosition(
        async (pos) => {
          await api.post("/attendance/checkout", {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          });
          toast.success("Checked out");
          setDismissed(true);
          refresh();
        },
        async () => {
          await api.post("/attendance/checkout", {});
          toast.success("Checked out");
          setDismissed(true);
          refresh();
        },
        { timeout: 4000 },
      );
    } catch (e) {
      toast.error(parseApiError(e, "Check-out failed"));
    }
  };

  const stillWorking = () => {
    try { sessionStorage.setItem("pulse-checkin-reminder-dismissed", "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="sticky top-0 z-30 bg-amber-50 border-b border-amber-300 text-amber-900 px-4 py-2 flex items-center justify-between gap-3 text-xs"
      data-testid="open-checkin-banner"
    >
      <div className="inline-flex items-center gap-2 min-w-0">
        <Clock size={14} className="shrink-0" />
        <span className="truncate">
          <span className="font-semibold">You're still checked in.</span>
          <span className="hidden sm:inline"> If you're done for the day, please mark check-out so attendance is recorded correctly.</span>
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={checkoutNow}
          className="px-2.5 py-1 rounded-sm bg-[#0a2540] text-white hover:bg-[#0f2f50]"
          data-testid="btn-banner-checkout"
        >
          Check out now
        </button>
        <button
          onClick={stillWorking}
          className="px-2.5 py-1 rounded-sm border border-amber-400 hover:bg-amber-100"
          data-testid="btn-banner-dismiss"
        >
          Still working
        </button>
        <button
          onClick={stillWorking}
          className="p-1 text-amber-700 hover:text-amber-900"
          aria-label="Close"
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
}
